import { Stack } from '@mui/material';
import { first } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { SectionHeaderWithAccent } from '@/components/display/SectionHeaderWithAccent/SectionHeaderWithAccent';
import { Button } from '@/components/form/baseInputs/Button';
import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup';
import {
  FullScreenModal,
  FullScreenModalProps,
} from '@/components/modals/FullScreenModal/FullScreenModal';
import { Footer } from '@/components/navigation/Footer';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { SplitScreenLayoutWithDocuments } from '@/modules/documents/components/SplitScreenLayoutWithDocuments/SplitScreenLayoutWithDocuments';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { DispositiveProvisionTemplateKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '../DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import {
  DispositiveProvisionsFormRecipient,
  DispositiveProvisionsModalDetails,
} from '../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { SURVIVING_SPOUSE_SENTINEL } from '../DispositiveProvisionsForm/hooks/useRecipientOptions';
import {
  DispositiveProvisionsTemplateAdvancedSettings,
  DispositiveProvisionsTemplateEditorTab,
  DispositiveProvisionsTemplateHeader,
  DispositiveProvisionsTemplateHeaderContextMenu,
  DispositiveProvisionsTemplateLinkedEntities,
  TemplateUseCountBadge, //   DispositiveProvisionsTemplateRecipients,
} from './DispositiveProvisionsTemplateSplitScreenModal.components';
import { DispositiveProvisionsTemplateSplitScreenModalContextProvider } from './DispositiveProvisionsTemplateSplitScreenModal.context';
import {
  DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE,
  DispositiveProvisionsTemplateShape,
  DispositiveProvisionTemplateDetails,
  NEW_TEMPLATE_SENTINEL,
} from './DispositiveProvisionsTemplateSplitScreenModal.types';
import {
  getDefaultValues,
  mapFormDataToCreatePayload,
  mapFormDataToUpdatePayload,
  mapQueryDataToForm,
} from './DispositiveProvisionsTemplateSplitScreenModal.utils';
import {
  useCreateDispositiveProvisionsTemplateMutation,
  useDeleteDispositiveProvisionsTemplateMutation,
  useUpdateDispositiveProvisionTemplateMutation,
} from './graphql/DispositiveProvisionsTemplateMutations.generated';
import { useDispositiveProvisionTemplateDataQuery } from './graphql/DispositiveProvisionTemplateData.generated';
import { useRecipientDisplayName } from './hooks/useRecipientDisplayName';

interface DispositiveProvisionsTemplateSplitScreenModalInnerProps
  extends FullScreenModalProps {
  onClose: (
    templateId?: string,
    recipieints?: DispositiveProvisionsFormRecipient[],
    isDeleted?: boolean
  ) => void;
  isFromEditingDispositions?: boolean;
  setDispositiveProvisionsModalDetails?: (
    details: DispositiveProvisionsModalDetails | null
  ) => void;
}

enum Tabs {
  Edit = 'edit',
  Links = 'links',
}

function DispositiveProvisionsTemplateSplitScreenModalInner({
  isFromEditingDispositions = false,
  setDispositiveProvisionsModalDetails,
  ...modalProps
}: DispositiveProvisionsTemplateSplitScreenModalInnerProps) {
  const canEditDP = useCanEditDispositiveProvisions();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { householdId } = useHouseholdDetailsContext();
  const [currentTab, setCurrentTab] = useState<Tabs>(Tabs.Edit);

  const { control, setValue, handleSubmit, formState } =
    useFormContext<DispositiveProvisionsTemplateShape>();

  const { isLoading, isSubmitting } = formState;

  const [createTemplate] = useCreateDispositiveProvisionsTemplateMutation({
    refetchQueries: ['DispositiveProvisionTemplates'],
    onCompleted: () => {
      showFeedback('Dispositive provision template created successfully', {
        variant: 'success',
      });
    },
    onError: (error) => {
      reportError(
        'Caught error while creating new dispositive provision template',
        error
      );
      showFeedback('An error occurred.  Please try again later.');
    },
  });

  const [updateTemplate] = useUpdateDispositiveProvisionTemplateMutation({
    refetchQueries: [
      'DispositiveProvisionTemplates',
      'DispositiveProvisionTemplateData', // need to cache bust the current data, otherwise the next open will load stale data
    ],
    onError: (error) => {
      showFeedback('An error occurred.  Please try again later.');
      reportError(
        'Caught error while updating dispositive provision template',
        error
      );
    },
    onCompleted: () => {
      showFeedback('Dispositive provision template updated', {
        variant: 'success',
      });
    },
  });

  const [deleteTemplate] = useDeleteDispositiveProvisionsTemplateMutation({
    refetchQueries: [
      'DispositiveProvisionTemplates',
      'ManageDispositions', // need to cache bust the currently-applied templates to reflect the deleted template
      'DispositiveProvisionTemplateData', // need to cache bust the current data, otherwise the next open will load stale data
    ],
    onError: (error) => {
      showFeedback('An error occurred.  Please try again later.');
      reportError(
        'Caught error while deleting dispositive provision template',
        error
      );
    },
    onCompleted: () => {
      showFeedback('Dispositive provision template deleted', {
        variant: 'success',
      });
    },
  });

  const [documentId, templateId] = useWatch({
    control,
    name: [
      `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.documentId`,
      `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.id`,
    ],
  });

  const isEditingExistingTemplate = templateId !== NEW_TEMPLATE_SENTINEL;

  const onDelete = useCallback(async () => {
    if (!canEditDP) return;
    try {
      await deleteTemplate({
        variables: {
          id: templateId,
        },
      });
      modalProps.onClose?.(undefined, undefined, true);
    } catch {
      // Error is already handled by the mutation's onError callback;
      // catch here to prevent an unhandled promise rejection.
    }
  }, [canEditDP, deleteTemplate, modalProps, templateId]);

  const onSubmit = handleSubmit(async (data) => {
    if (!canEditDP) return;
    if (!householdId) {
      showFeedback('Please try again later');
      reportError(
        'Could not get the household ID to create a new dispositive provision template'
      );
      return;
    }

    if (
      data[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE].id ===
      NEW_TEMPLATE_SENTINEL
    ) {
      const payload = mapFormDataToCreatePayload(data, householdId);
      const result = await createTemplate({
        variables: {
          input: payload,
        },
      });
      modalProps.onClose?.(
        result.data?.createDispositiveProvisionTemplate.id,
        data[DISPOSITIVE_PROVISIONS_FORM_NAMESPACE].recipients
      );
    } else {
      const payload = mapFormDataToUpdatePayload(data, householdId);
      await updateTemplate({
        variables: {
          input: payload,
        },
      });
      modalProps.onClose?.(
        data[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE].id,
        data[DISPOSITIVE_PROVISIONS_FORM_NAMESPACE].recipients
      );
    }
  });

  return (
    <FullScreenModal {...modalProps}>
      <SplitScreenLayoutWithDocuments
        showAllHouseholdDocuments
        householdId={householdId}
        header={
          <SectionHeaderWithAccent
            containerProps={{
              pt: 1.5,
              pb: 1.5,
              pl: 2,
              pr: 2,
            }}
            header={{
              heading: <DispositiveProvisionsTemplateHeader />,
            }}
            rightHeaderContent={
              isEditingExistingTemplate && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <TemplateUseCountBadge />
                  {canEditDP && (
                    <DispositiveProvisionsTemplateHeaderContextMenu
                      onDelete={onDelete}
                    />
                  )}
                </Stack>
              )
            }
          />
        }
        rightContent={
          <Stack
            spacing={2}
            sx={{
              p: 2,
              overflowY: 'auto',
              flexGrow: 1,
              minHeight: 0,
            }}
          >
            {isEditingExistingTemplate && (
              <ButtonGroup<Tabs>
                label={''}
                hideLabel
                value={currentTab}
                onChange={(_event, value) => setCurrentTab(value)}
                options={[
                  {
                    display: 'Edit template',
                    value: Tabs.Edit,
                  },
                  {
                    display: 'Linked entities',
                    value: Tabs.Links,
                  },
                ]}
              />
            )}
            <DispositiveProvisionsTemplateAdvancedSettings />
            {currentTab === Tabs.Links && (
              <DispositiveProvisionsTemplateLinkedEntities
                isFromEditingDispositions={isFromEditingDispositions}
                setDispositiveProvisionsModalDetails={
                  setDispositiveProvisionsModalDetails
                }
              />
            )}
            {currentTab === Tabs.Edit && (
              <DispositiveProvisionsTemplateEditorTab />
            )}
          </Stack>
        }
        overrideActiveDocumentId={documentId}
        onUpdateActiveDocumentId={({ documentId }) =>
          setValue(
            `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.documentId`,
            documentId
          )
        }
        footer={
          <Footer
            rightAction={
              <Stack
                spacing={1}
                alignItems="center"
                justifyContent="flex-end"
                direction="row"
              >
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => modalProps.onClose()}
                >
                  {canEditDP ? 'Cancel' : 'Close'}
                </Button>
                {canEditDP && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onSubmit}
                    loading={isLoading || isSubmitting}
                  >
                    {isEditingExistingTemplate
                      ? 'Save changes'
                      : 'Create template'}
                  </Button>
                )}
              </Stack>
            }
          />
        }
      />
    </FullScreenModal>
  );
}

interface DispositiveProvisionsSplitScreenModalProps
  extends DispositiveProvisionsTemplateSplitScreenModalInnerProps {
  templateDetails: DispositiveProvisionTemplateDetails;
}

export function DispositiveProvisionsTemplateSplitScreenModal({
  templateDetails,
  ...modalProps
}: DispositiveProvisionsSplitScreenModalProps) {
  const canEdit = useCanEditDispositiveProvisions();
  const { templateId, templateType } = templateDetails;
  const formData = useForm<DispositiveProvisionsTemplateShape>({
    defaultValues: getDefaultValues(templateDetails),
  });

  const { control, reset, setValue } = formData;

  const recipients = useWatch({
    control,
    name: `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients`,
  });
  const currentDisplayName = useWatch({
    control,
    name: `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.displayName`,
  });

  const selectedPouroverWillRecipientId = recipients?.[0]?.recipientId;

  // Get display name directly from Apollo cache using node interface
  const { displayName: recipientDisplayName } = useRecipientDisplayName(
    selectedPouroverWillRecipientId === SURVIVING_SPOUSE_SENTINEL
      ? null
      : selectedPouroverWillRecipientId || null
  );

  useEffect(() => {
    // Update the template name when the recipient is changed
    if (
      templateId === NEW_TEMPLATE_SENTINEL &&
      templateType === DispositiveProvisionTemplateKind.PouroverWill &&
      selectedPouroverWillRecipientId
    ) {
      let displayName: string | null = null;

      if (selectedPouroverWillRecipientId === SURVIVING_SPOUSE_SENTINEL) {
        displayName = 'Surviving Spouse';
      } else {
        displayName = recipientDisplayName;
      }

      if (displayName) {
        const newName = `Pourover will to ${displayName}`;
        // Only update if the name has actually changed to avoid unnecessary re-renders
        if (newName !== currentDisplayName) {
          setValue(
            `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.displayName`,
            newName
          );
        }
      }
    }
  }, [
    currentDisplayName,
    recipientDisplayName,
    selectedPouroverWillRecipientId,
    setValue,
    templateId,
    templateType,
  ]);

  const { data, loading } = useDispositiveProvisionTemplateDataQuery({
    fetchPolicy: 'network-only', // always fetch the latest data before editing
    variables: {
      templateWhere: {
        id: templateId,
      },
    },
  });

  useEffect(() => {
    if (loading) {
      return;
    }

    const existingTemplate = first(
      getNodes(data?.dispositiveProvisionTemplates)
    );

    if (existingTemplate) {
      reset(mapQueryDataToForm(existingTemplate));
    } else if (
      templateType ===
      DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse
    ) {
      setValue(
        `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients.0.recipientId`,
        SURVIVING_SPOUSE_SENTINEL
      );
    }
  }, [data, loading, reset, setValue, templateType]);

  if (!canEdit && templateId === NEW_TEMPLATE_SENTINEL) return null;

  return (
    <DispositiveProvisionsTemplateSplitScreenModalContextProvider
      isTemplateMode
    >
      <FormProvider {...formData}>
        <HouseholdPermissionAwareDisabledProvider forceDisabled={!canEdit}>
          <DispositiveProvisionsTemplateSplitScreenModalInner {...modalProps} />
        </HouseholdPermissionAwareDisabledProvider>
      </FormProvider>
    </DispositiveProvisionsTemplateSplitScreenModalContextProvider>
  );
}
